import { useState, useEffect, useCallback } from 'react';
import { X, Building2, Loader2, TrendingUp, AlertTriangle, CheckCircle2, DollarSign, Clock } from 'lucide-react';
import api from '../../lib/api';
import { AlegraStatusBadge } from './AlegraStatusBadge';
import { DteDocsActions } from './DteDocsActions';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('es-CL');

/**
 * Iter 235.e (Carlos): Modal con historial de facturas del proveedor.
 *
 * Se abre al hacer click en el RUT de cualquier proveedor desde:
 * - RegistroDTE
 * - SeguimientoFacturas (buscador global)
 * - CxPPanel
 * - AceptaRechazoDTE
 *
 * Props:
 *   open: boolean
 *   rut: string
 *   onClose: () => void
 */
export function ProveedorHistorialModal({ open, rut, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!rut) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/contabilidad/proveedor/${encodeURIComponent(rut)}/historial`, {
        params: { limit: 30 },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [rut]);

  useEffect(() => {
    if (open && rut) load();
  }, [open, rut, load]);

  if (!open) return null;

  const stats = data?.stats || {};
  const prov = data?.proveedor || {};
  const items = data?.resultados || [];

  const pct = stats.total_facturado > 0
    ? Math.round((stats.total_pendiente / stats.total_facturado) * 100)
    : 0;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`prov-historial-${rut}`}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b px-4 py-3 flex items-center justify-between z-10">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Historial del proveedor
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              {prov.razon_social || '—'} ·{' '}
              <span className="font-mono">{prov.rut || rut}</span>
              {prov.comuna && <> · 📍 {prov.comuna}</>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/60"
            data-testid="close-prov-historial"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              <span className="ml-2 text-sm text-gray-500">Consultando últimas {30} facturas…</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 inline mr-1" /> {error}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>Sin facturas para este proveedor.</p>
            </div>
          ) : (
            <>
              {/* Stats cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <p className="text-[9px] uppercase text-emerald-700 font-bold flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Pagadas
                  </p>
                  <p className="text-xl font-black text-emerald-800">{stats.pagadas || 0}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <p className="text-[9px] uppercase text-amber-700 font-bold flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Pendientes
                  </p>
                  <p className="text-xl font-black text-amber-800">{stats.pendientes || 0}</p>
                  <p className="text-[9px] text-amber-700">${fmt(stats.total_pendiente || 0)}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-[9px] uppercase text-red-700 font-bold flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Vencidas
                  </p>
                  <p className="text-xl font-black text-red-800">{stats.vencidas || 0}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <p className="text-[9px] uppercase text-blue-700 font-bold flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> En Alegra
                  </p>
                  <p className="text-xl font-black text-blue-800">{stats.contabilizadas || 0}</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded p-2">
                  <p className="text-[9px] uppercase text-purple-700 font-bold flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Total facturado
                  </p>
                  <p className="text-lg font-black text-purple-800 tabular-nums">${fmt(stats.total_facturado || 0)}</p>
                  <p className="text-[9px] text-purple-700">{pct}% pendiente</p>
                </div>
              </div>

              {/* Timeline tabla */}
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-gray-600 text-[10px] uppercase">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Folio</th>
                      <th className="px-2 py-1.5 text-left">F. Emisión</th>
                      <th className="px-2 py-1.5 text-left">F. Vencimiento</th>
                      <th className="px-2 py-1.5 text-right">Total</th>
                      <th className="px-2 py-1.5 text-center">OC / Recep</th>
                      <th className="px-2 py-1.5 text-center">Estado</th>
                      <th className="px-2 py-1.5 text-center">Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const venc = it.fecha_vencimiento || '';
                      const isVenc = venc && new Date(venc) < new Date() && it.estado === 'pendiente_pago';
                      return (
                        <tr key={it.folio} className="border-t hover:bg-slate-50">
                          <td className="px-2 py-1.5 font-mono font-bold text-indigo-700">{it.folio}</td>
                          <td className="px-2 py-1.5 text-gray-600">{it.fecha_emision || '—'}</td>
                          <td className={`px-2 py-1.5 ${isVenc ? 'text-red-700 font-semibold' : 'text-gray-600'}`}>
                            {venc || '—'}
                            {it.term_pago_dias > 0 && (
                              <span className="ml-1 text-[8px] text-purple-600">({it.term_pago_dias}d)</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">${fmt(it.total)}</td>
                          <td className="px-2 py-1.5 text-center text-[10px]">
                            {it.orden_compra && <div className="text-green-700 font-mono">OC {it.orden_compra}</div>}
                            {it.numero_recepcion && <div className="text-purple-700 font-mono">{it.numero_recepcion}</div>}
                            {!it.orden_compra && !it.numero_recepcion && <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <AlegraStatusBadge
                              contabilizada_en_alegra={Boolean(it.alegra_id)}
                              contabilizada={Boolean(it.alegra_id) || it.estado === 'contabilizada'}
                              estado_pago={it.estado === 'pagada' ? 'pagada' : (it.estado === 'pendiente_pago' ? 'pendiente' : '')}
                              alegra_bill_id={it.alegra_id}
                              en_nomina={it.en_nomina}
                              liberada_para_pago={it.liberada_para_pago}
                              size="xs"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <DteDocsActions
                              folio={it.folio}
                              rutEmisor={prov.rut || rut}
                              numeroOc={it.orden_compra}
                              numeroRecepcion={it.numero_recepcion}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="text-[10px] text-gray-500 mt-2 italic">
                Mostrando últimas {items.length} facturas. Click en folio para verla en detalle.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProveedorHistorialModal;
